/**
 * EnquiryCategoryManager — owner/manager-only dialog for CRUD on
 * enquiry categories. Includes a "Seed defaults" button that adds
 * the spec-recommended catalogue idempotently.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Sparkles } from "lucide-react";


const PRIORITIES = ["low", "normal", "high", "urgent"];


export default function EnquiryCategoryManager({ onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | category obj

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/enquiry-categories");
      setRows(r.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function seed() {
    try {
      const r = await api.post("/enquiry-categories/seed-defaults", {});
      toast.success(`Added ${r.data?.added || 0} default categories`);
      load(); onChanged?.();
    } catch (e) {
      toast.error("Seed failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function del(c) {
    if (!window.confirm(`Archive category "${c.name}"?`)) return;
    try {
      await api.delete(`/enquiry-categories/${c.id}`);
      toast.success("Archived");
      load(); onChanged?.();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  const grouped = useMemo(() => {
    const g = {};
    for (const c of rows) {
      const key = c.group || "Uncategorised";
      g[key] = g[key] || [];
      g[key].push(c);
    }
    return g;
  }, [rows]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="category-manager">
        <DialogHeader>
          <DialogTitle>Enquiry categories</DialogTitle>
          <DialogDescription className="text-xs">
            Configure the picker used in the New enquiry form. Changes don&apos;t
            rewrite historical reporting labels.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-3">
          <Button
            onClick={() => setEditing("new")}
            className="bg-zinc-950 text-white text-xs"
            data-testid="category-new-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> New category
          </Button>
          <Button variant="outline" onClick={seed} className="text-xs" data-testid="category-seed-btn">
            <Sparkles className="h-3.5 w-3.5 mr-1" /> Seed defaults
          </Button>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-zinc-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center border border-dashed border-zinc-200 rounded-md text-sm text-zinc-500">
            No categories yet — click <b>Seed defaults</b> for a starter set.
          </div>
        ) : (
          Object.keys(grouped).sort().map((group) => (
            <div key={group} className="mb-3">
              <div className="text-2xs uppercase tracking-wider text-zinc-500 font-medium mb-1">{group}</div>
              <ul className="space-y-1">
                {grouped[group].map((c) => (
                  <li key={c.id} className="flex items-center gap-2 text-sm border border-zinc-200 rounded-md px-2 py-1" data-testid={`category-row-${c.id}`}>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-2xs uppercase tracking-wider text-zinc-500">{c.default_priority}</span>
                    {c.response_target_hours && (
                      <span className="text-2xs text-zinc-500">· {c.response_target_hours}h</span>
                    )}
                    <button
                      onClick={() => setEditing(c)}
                      className="text-xs text-zinc-600 hover:text-zinc-900 underline"
                      data-testid={`category-edit-${c.id}`}
                    >Edit</button>
                    <button
                      onClick={() => del(c)}
                      className="text-red-700 hover:bg-red-50 rounded p-1"
                      data-testid={`category-delete-${c.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}

        {editing && (
          <CategoryEditor
            cat={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); onChanged?.(); }}
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CategoryEditor({ cat, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: cat?.name || "",
    group: cat?.group || "",
    default_priority: cat?.default_priority || "normal",
    response_target_hours: cat?.response_target_hours || "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        group: form.group.trim() || null,
        default_priority: form.default_priority,
        response_target_hours: form.response_target_hours ? Number(form.response_target_hours) : null,
      };
      if (cat) {
        await api.patch(`/enquiry-categories/${cat.id}`, payload);
      } else {
        await api.post("/enquiry-categories", payload);
      }
      toast.success("Saved");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="category-editor">
        <DialogHeader>
          <DialogTitle>{cat ? "Edit category" : "New category"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name" />
          </div>
          <div>
            <Label className="text-2xs">Group</Label>
            <Input value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} placeholder="e.g. New service enquiry" data-testid="category-group" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-2xs">Default priority</Label>
              <select
                value={form.default_priority}
                onChange={(e) => setForm({ ...form, default_priority: e.target.value })}
                className="mt-1 h-9 w-full px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="category-priority"
              >
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-2xs">Response target (hours)</Label>
              <Input
                type="number"
                value={form.response_target_hours}
                onChange={(e) => setForm({ ...form, response_target_hours: e.target.value })}
                data-testid="category-response-hours"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-zinc-950 text-white" data-testid="category-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
